// src/components/dashboards/add_patient_v2/components/AddPatientsDropzone.tsx
// -----------------------------------------------------------------------------
// AddPatientsDropzone — compact single-row "Add patients" affordance.
//
// V2-Rev2 redesign (Joan, 2026-05-23): the previous ~210 px panel (title +
// hint + 2 buttons + own padded box) collapses to a single ~40 px row that
// is both a tap target and a drop target. The tap target on the left fires
// the manual "add empty card" action; the right side surfaces the multi-
// patient drop hint and accepts folders / ZIPs / CSV / multiple PDFs.
//
// Drop-routing tree unchanged (PDF/folder/ZIP/Excel/mixed) — only the visual
// treatment is shrunken.
//
// On drag-over the whole row lights up (bg-tint + dashed primary border).
// No center overlay text.
// -----------------------------------------------------------------------------

import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import React, { ChangeEvent, useRef } from "react";

import { useTranslation } from "../../../../i18n";
import { FolderNode, GlobalDropKind } from "../types";
// Re-export so callers that imported FolderNode from this module keep working.
export type { FolderNode } from "../types";

const TABULAR_EXTENSIONS = new Set([".csv", ".xlsx", ".xls"]);
const ZIP_EXTENSIONS = new Set([".zip"]);

function fileExt(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot).toLowerCase() : "";
}

export function classifyDrop(
  loose: File[],
  folders: Record<string, File[]>,
  tree?: FolderNode[],
): GlobalDropKind {
  const hasFolders = Object.keys(folders).length > 0;
  const hasFiles = loose.length > 0;
  if (!hasFolders && !hasFiles) return { kind: "empty" };
  if (hasFolders && hasFiles) {
    return { kind: "mixed", reason: "folder_plus_files" };
  }
  if (hasFolders) {
    return { kind: "folders", foldersByTopLevel: folders, tree };
  }
  if (loose.length === 1) {
    const ext = fileExt(loose[0].name);
    if (TABULAR_EXTENSIONS.has(ext)) return { kind: "tabular", file: loose[0] };
    if (ZIP_EXTENSIONS.has(ext)) return { kind: "zip", file: loose[0] };
  }
  const anyTabular = loose.some((f) => TABULAR_EXTENSIONS.has(fileExt(f.name)));
  if (anyTabular && loose.length > 1) {
    return { kind: "mixed", reason: "tabular_plus_others" };
  }
  return { kind: "pdfs", files: loose };
}

/** Flatten a FolderNode's files (including all descendants) into a single
 *  array — used to behave like the legacy flat-folders reader. */
export function flattenFolderFiles(node: FolderNode): File[] {
  const acc: File[] = [...node.files];
  for (const sub of node.subfolders) acc.push(...flattenFolderFiles(sub));
  return acc;
}

/** True iff this folder has at least one nested subfolder. */
export function hasSubfolders(node: FolderNode): boolean {
  return node.subfolders.length > 0;
}

export interface AddPatientsDropzoneProps {
  onDropClassified: (result: GlobalDropKind) => void;
  disabled?: boolean;
  /** Highlight signal — true while a file drag hovers the bulk zone. Drops
   *  themselves are routed centrally by AddPatientFormV2 (hit-testing), so
   *  this component owns NO drop handlers (Joan, 2026-05-27). It keeps only
   *  the click-to-browse file/folder pickers. */
  isDropActive?: boolean;
}

const AddPatientsDropzone: React.FC<AddPatientsDropzoneProps> = ({
  onDropClassified,
  disabled,
  isDropActive = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDragging = isDropActive;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const handleFilePickerChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onDropClassified(classifyDrop(Array.from(e.target.files), {}));
      e.target.value = "";
    }
  };

  const handleFolderPickerChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Build BOTH a flat folders dict (legacy) and a nested tree (for the
      // folder-level picker) from each file's webkitRelativePath.
      const folders: Record<string, File[]> = {};
      const treeMap = new Map<string, FolderNode>();
      const ensureNode = (
        parent: FolderNode | null,
        name: string,
      ): FolderNode => {
        if (parent) {
          let child = parent.subfolders.find((s) => s.name === name);
          if (!child) {
            child = { name, files: [], subfolders: [] };
            parent.subfolders.push(child);
          }
          return child;
        }
        let root = treeMap.get(name);
        if (!root) {
          root = { name, files: [], subfolders: [] };
          treeMap.set(name, root);
        }
        return root;
      };
      for (let i = 0; i < e.target.files.length; i++) {
        const f = e.target.files[i];
        const rel =
          (f as File & { webkitRelativePath?: string }).webkitRelativePath ||
          f.name;
        const parts = rel.split("/").filter(Boolean);
        if (parts.length === 0) continue;
        const top = parts[0] || "Folder";
        if (!folders[top]) folders[top] = [];
        folders[top].push(f);
        // Walk the tree creating dirs as we go; last segment is the file.
        let node = ensureNode(null, top);
        for (let p = 1; p < parts.length - 1; p++) {
          node = ensureNode(node, parts[p]);
        }
        node.files.push(f);
      }
      const tree = Array.from(treeMap.values());
      onDropClassified(classifyDrop([], folders, tree));
      e.target.value = "";
    }
  };

  // Per-card cloud-upload IconButton styling (Joan, 2026-05-26): use the
  // same aesthetic for the dropzone's file + folder buttons so the
  // affordances feel like one family.
  const cardUploadBtnSx = {
    width: 36,
    height: 36,
    borderRadius: 1.5,
    bgcolor: alpha(theme.palette.info.main, 0.1),
    color: theme.palette.info.main,
    border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
    backdropFilter: "blur(8px)",
    transition: "all 0.2s",
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, 0.18),
      color: theme.palette.primary.main,
      borderColor: alpha(theme.palette.primary.main, 0.5),
      transform: "translateY(-1px)",
    },
    "&.Mui-disabled": { opacity: 0.45 },
  } as const;

  return (
    <Box
      data-bulk-dropzone="true"
      role="region"
      aria-label={t("patients_v2.global_dropzone_title", "Add more patients")}
      sx={{
        // Joan, 2026-05-28: tint the resting dropzone with the same hue as the
        // patient card body (primary.dark) but a touch more transparent (0.06
        // vs the card body's 0.10) so it reads as a lighter sibling of the
        // cards instead of the brighter paper fill it had before.
        bgcolor: isDragging
          ? alpha(theme.palette.primary.main, 0.05)
          : alpha(theme.palette.primary.dark, 0.06),
        // Dashed at rest to signal "you can drop files here"; solid primary while
        // dragging (Joan, 2026-05-29).
        border: `1px ${isDragging ? "solid" : "dashed"} ${
          isDragging
            ? theme.palette.primary.main
            : alpha(theme.palette.primary.main, 0.35)
        }`,
        borderRadius: 2,
        py: { xs: 1.75, sm: 2 },
        px: { xs: 1.5, sm: 2.5 },
        transition: "background-color 0.15s, border-color 0.15s",
        "&:hover": disabled
          ? undefined
          : {
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              borderColor: theme.palette.primary.main,
            },
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      {/* LEFT column — descriptive text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: "text.secondary",
            lineHeight: 1.3,
          }}
        >
          {t(
            "patients_v2.global_dropzone_hint",
            "Drop folders, ZIPs, multiple PDFs, or a CSV/Excel here.",
          )}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.25 }}
        >
          PDF · Word · Excel · Images · WSI · folder · ZIP · CSV
        </Typography>
      </Box>

      {/* RIGHT column — file + folder buttons, same aesthetic as the
          per-card cloud-upload IconButton. */}
      <Box
        sx={{ display: "flex", gap: 1, flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip
          title={t("patients_v2.global_dropzone_browse_files", "Browse files")}
          arrow
        >
          <span>
            <IconButton
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
              aria-label={t(
                "patients_v2.global_dropzone_browse_files",
                "Browse files",
              )}
              sx={cardUploadBtnSx}
            >
              <CloudUploadIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={t(
            "patients_v2.global_dropzone_browse_folder",
            "Browse folder",
          )}
          arrow
        >
          <span>
            <IconButton
              disabled={disabled}
              onClick={() => folderInputRef.current?.click()}
              aria-label={t(
                "patients_v2.global_dropzone_browse_folder",
                "Browse folder",
              )}
              sx={cardUploadBtnSx}
            >
              <FolderOpenIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFilePickerChange}
        style={{ display: "none" }}
        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.zip,.tiff,.tif"
      />
      <input
        ref={folderInputRef}
        type="file"
        onChange={handleFolderPickerChange}
        style={{ display: "none" }}
        {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
        multiple
      />
    </Box>
  );
};

// Memoized: props (onDropClassified is a stable useCallback, disabled,
// isDropActive) only change on real transitions, so the dropzone's blurred
// surface isn't needlessly repainted on every parent re-render during a drag.
export default React.memo(AddPatientsDropzone);
